import { adjusted, luminance, mix, NON_TEXT, parseHex, readableOn, TEXT, toHex, WHITE, type Rgb } from "./wcag-contrast";

/**
 * The brand colour roles (docs/brand-colour-tokens.md, the contract every platform implements): every colour
 * a brand gives the screens, derived from the manifest's `colors` by one pure function. Fills keep the brand's
 * own accent and carry `onAccent` ink; only colour that stands on the page by itself (text, icons, lines,
 * focus) is shaded until it reads. No React, no CSS: `brand.ts` turns these into the kit's variables.
 */

/** The design system's fallback accent, for a manifest colour that is missing or not `#rrggbb`. */
export const FALLBACK_ACCENT = "#2a85ff";
/** The dark apps' page ground, when the manifest names none and the caller passes no ground of its own. */
export const DEFAULT_GROUND = "#0c0c0c";

/** The manifest's `colors` (accent is required there; anything unparsable falls back, never throws). */
export interface BrandColors {
  accent?: string | null;
  background?: string | null;
  surface?: string | null;
}

/** Every role, as `#rrggbb`. The surfaces exist on dark grounds only (null on a light one). */
export interface ColorTokens {
  /** Fills: primary buttons, badges, selected pills, switches, progress. Always the brand's own colour. */
  accent: string;
  /** Ink on `accent` fills. */
  onAccent: string;
  /** Words and icons in the brand colour (4.5:1 on the ground). */
  accentText: string;
  /** Focus rings, outlines, thin indicators (3:1 on the ground). */
  accentStroke: string;
  /** Tonal buttons, selected rows and pills. */
  accentContainer: string;
  /** Ink on `accentContainer`. */
  onAccentContainer: string;
  /** The brand gradient (rank numerals, loaders, no-artwork fallbacks). */
  gradientStart: string;
  gradientEnd: string;
  /** The page ground's brand wash. */
  pageGradientTop: string;
  pageGradientBottom: string;
  surface1: string | null;
  surface2: string | null;
  surface3: string | null;
}

/** The roles a theme sets, in the order they are written out (the surfaces are the dark apps' own). */
export const BRAND_ROLES = [
  "accent", "onAccent", "accentText", "accentStroke", "accentContainer", "onAccentContainer",
  "gradientStart", "gradientEnd", "pageGradientTop", "pageGradientBottom",
] as const satisfies readonly (keyof ColorTokens)[];

const FALLBACK: Rgb = parseHex(FALLBACK_ACCENT)!;
const DEFAULT: Rgb = parseHex(DEFAULT_GROUND)!;

/** A role standing on `ground` by itself: the brand shaded to `minimum`, else the fallback shaded, else black or white. */
const standing = (brand: Rgb, ground: Rgb, minimum: number): Rgb =>
  adjusted(brand, ground, minimum) ?? adjusted(FALLBACK, ground, minimum) ?? readableOn(ground);

/**
 * The roles for one ground. `ground` (`#rrggbb`) overrides the manifest's `background`: the portal derives
 * twice, against its own light and dark grounds; the apps pass none and use the manifest's.
 */
export function deriveColorTokens(colors: BrandColors, ground?: string): ColorTokens {
  const B = parseHex(ground) ?? parseHex(colors.background) ?? DEFAULT;
  const A = parseHex(colors.accent) ?? FALLBACK;
  const container = mix(B, A, 0.24);
  const dark = luminance(B) < 0.5;
  const surface1 = dark ? (parseHex(colors.surface) ?? mix(B, WHITE, 0.06)) : null;
  return {
    accent: toHex(A),
    onAccent: toHex(readableOn(A)),
    accentText: toHex(standing(A, B, TEXT)),
    accentStroke: toHex(standing(A, B, NON_TEXT)),
    accentContainer: toHex(container),
    onAccentContainer: toHex(adjusted(A, container, TEXT) ?? readableOn(container)),
    gradientStart: toHex(A),
    gradientEnd: toHex(mix(A, B, 0.45)),
    pageGradientTop: toHex(mix(B, A, 0.1)),
    pageGradientBottom: toHex(B),
    surface1: surface1 && toHex(surface1),
    surface2: surface1 && toHex(mix(surface1, WHITE, 0.05)),
    surface3: surface1 && toHex(mix(surface1, WHITE, 0.1)),
  };
}

/** `accentText` → `--accent-text`: a role's CSS custom property, the same name on every web surface. */
export const roleVariable = (role: keyof ColorTokens): string => `--${role.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`;
